import { BaseAction } from './ActionInterface';
import { ActionTypes } from './ActionTypes';
import { BeerState } from '../types';

/**
 * Set Search Term Action
 * Updates the search term filter
 */
export class SetSearchTermAction extends BaseAction<string> {
  type = ActionTypes.SET_SEARCH_TERM;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        searchTerm: this.payload || '',
      },
    };
  }
}

/**
 * Set Type Filter Action
 * Updates the beer type filter
 */
export class SetTypeFilterAction extends BaseAction<string> {
  type = ActionTypes.SET_TYPE_FILTER;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        typeFilter: this.payload || '',
      },
    };
  }
}

/**
 * Set Brewery Filter Action
 * Updates the brewery filter
 */
export class SetBreweryFilterAction extends BaseAction<string> {
  type = ActionTypes.SET_BREWERY_FILTER;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        breweryFilter: this.payload || '',
      },
    };
  }
}

/**
 * Set Drink Type Filter Action
 * Updates the drink type filter (beer, cider, etc.)
 */
export class SetDrinkTypeFilterAction extends BaseAction<string> {
  type = ActionTypes.SET_DRINK_TYPE_FILTER;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        drinkTypeFilter: this.payload || '',
      },
    };
  }
}

/**
 * Set Availability Filter Action
 * Updates the availability filter
 */
export class SetAvailabilityFilterAction extends BaseAction<string> {
  type = ActionTypes.SET_AVAILABILITY_FILTER;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        availabilityFilter: this.payload || '',
      },
    };
  }
}

/**
 * Add Tag Filter Action
 * Adds a tag to the tag filters
 */
export class AddTagFilterAction extends BaseAction<string> {
  type = ActionTypes.ADD_TAG_FILTER;
  
  execute(state: BeerState): BeerState {
    const tag = this.payload;
    
    if (!tag) {
      return state;
    }
    
    // Defensive check to ensure state.filters exists
    if (!state.filters) {
      // Initialize filters if it doesn't exist
      return {
        ...state,
        filters: {
          searchTerm: '',
          typeFilter: '',
          breweryFilter: '',
          drinkTypeFilter: '',
          availabilityFilter: '',
          tagFilters: [tag], // Initialize with the new tag
          showOnlyFavorites: false,
          showOnlyAvailable: false,
          filterByFavorites: false
        }
      };
    }
    
    // Defensive check for tagFilters
    const currentTagFilters = state.filters.tagFilters || [];
    
    // Check if tag already exists
    if (currentTagFilters.includes(tag)) {
      return state;
    }
    
    return {
      ...state,
      filters: {
        ...state.filters,
        tagFilters: [...currentTagFilters, tag],
      },
    };
  }
}

/**
 * Remove Tag Filter Action
 * Removes a tag from the tag filters
 */
export class RemoveTagFilterAction extends BaseAction<string> {
  type = ActionTypes.REMOVE_TAG_FILTER;
  
  execute(state: BeerState): BeerState {
    const tag = this.payload;
    
    if (!tag) {
      return state;
    }
    
    // Defensive check to ensure state.filters exists
    if (!state.filters) {
      // Initialize filters if it doesn't exist
      return {
        ...state,
        filters: {
          searchTerm: '',
          typeFilter: '',
          breweryFilter: '',
          drinkTypeFilter: '',
          availabilityFilter: '',
          tagFilters: [], // Empty array since we're removing the tag
          showOnlyFavorites: false,
          showOnlyAvailable: false,
          filterByFavorites: false
        }
      };
    }
    
    // Defensive check for tagFilters
    const currentTagFilters = state.filters.tagFilters || [];
    
    return {
      ...state,
      filters: {
        ...state.filters,
        tagFilters: currentTagFilters.filter(t => t !== tag),
      },
    };
  }
}

/**
 * Clear Tag Filters Action
 * Removes all tag filters
 */
export class ClearTagFiltersAction extends BaseAction {
  type = ActionTypes.CLEAR_TAG_FILTERS;
  
  execute(state: BeerState): BeerState {
    // Defensive check to ensure state.filters exists
    if (!state.filters) {
      // Initialize filters if it doesn't exist
      return {
        ...state,
        filters: {
          searchTerm: '',
          typeFilter: '',
          breweryFilter: '',
          drinkTypeFilter: '',
          availabilityFilter: '',
          tagFilters: [], // Empty array for cleared tags
          showOnlyFavorites: false,
          showOnlyAvailable: false,
          filterByFavorites: false
        }
      };
    }
    
    return {
      ...state,
      filters: {
        ...state.filters,
        tagFilters: [],
      },
    };
  }
}

/**
 * Clear All Filters Action
 * Resets all filters to their default values
 */
export class ClearAllFiltersAction extends BaseAction {
  type = ActionTypes.CLEAR_ALL_FILTERS;
  
  execute(state: BeerState): BeerState {
    // Defensive check to ensure state.filters exists
    if (!state.filters) {
      // Initialize filters with default values
      return {
        ...state,
        filters: {
          searchTerm: '',
          typeFilter: '',
          breweryFilter: '',
          drinkTypeFilter: '',
          availabilityFilter: '',
          tagFilters: [],
          showOnlyFavorites: false,
          showOnlyAvailable: false,
          filterByFavorites: false
        }
      };
    }
    
    return {
      ...state,
      filters: {
        ...state.filters,
        searchTerm: '',
        typeFilter: '',
        breweryFilter: '',
        drinkTypeFilter: '',
        availabilityFilter: '',
        tagFilters: [],
      },
    };
  }
}

/**
 * Set Show Only Favorites Action
 * Updates the show only favorites filter
 */
export class SetShowOnlyFavoritesAction extends BaseAction<boolean> {
  type = ActionTypes.SET_SHOW_ONLY_FAVORITES;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        showOnlyFavorites: this.payload || false,
      },
    };
  }
}

/**
 * Set Show Only Available Action
 * Updates the show only available filter
 */
export class SetShowOnlyAvailableAction extends BaseAction<boolean> {
  type = ActionTypes.SET_SHOW_ONLY_AVAILABLE;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        showOnlyAvailable: this.payload || false,
      },
    };
  }
}

/**
 * Set Filter By Favorites Action
 * Toggles filtering to show only favorites in Tried Beers list
 */
export class SetFilterByFavoritesAction extends BaseAction<boolean> {
  type = ActionTypes.SET_FILTER_BY_FAVORITES;
  
  execute(state: BeerState): BeerState {
    return {
      ...state,
      filters: {
        ...state.filters,
        filterByFavorites: this.payload || false,
      },
    };
  }
}
